/**
 * Reports area (Spec 201 — observability)
 *
 * On-demand generation/download of the Compliance + Completion reports, and
 * management of scheduled, auto-emailed reports. Built from @/components/ui per
 * the design blueprint (Constitution XLV).
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Send, Pencil, Trash2, Mail } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Badge,
  Input,
  Select,
  Toggle,
  Modal,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';
import {
  reportsApi,
  downloadReport,
  frequencySummary,
  ScheduledReport,
  ScheduledReportInput,
  ReportType,
  ReportFormat,
  ReportFrequency,
} from '../../api/reports';
import { formatEastern } from '@/utils/dateFormatter';

const todayIso = (offsetDays = 0): string =>
  new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);

const emptyForm = (): ScheduledReportInput => ({
  name: '',
  reportType: 'compliance',
  format: 'pdf',
  frequency: 'weekly',
  hourUtc: 6,
  dayOfWeek: 1,
  dayOfMonth: 1,
  periodDays: 30,
  recipients: [],
  enabled: true,
});

export function ReportsArea(): React.ReactElement {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const qc = useQueryClient();
  const [schedQ, setSchedQ] = useState('');

  // The full report menu (compliance, completion, recovery, failures, rpo, … executive).
  const typesQ = useQuery({ queryKey: ['report-types'], queryFn: reportsApi.types });
  const typeOptions = (typesQ.data ?? []).map((t) => ({ value: t.key, label: t.label }));
  const typeDesc = (key: string): string => typesQ.data?.find((t) => t.key === key)?.description ?? '';

  // ── On-demand ────────────────────────────────────────────────────────────
  const [odType, setOdType] = useState<ReportType>('compliance');
  const [odFormat, setOdFormat] = useState<ReportFormat>('pdf');
  const [odStart, setOdStart] = useState(todayIso(-30));
  const [odEnd, setOdEnd] = useState(todayIso());
  const [downloading, setDownloading] = useState(false);
  const [odError, setOdError] = useState<string | null>(null);

  const runDownload = async () => {
    setDownloading(true);
    setOdError(null);
    try {
      await downloadReport(odType, odFormat, odStart, odEnd);
    } catch (e) {
      setOdError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // ── Scheduled ──────────────────────────────────────────────────────────────
  const schedules = useQuery({ queryKey: ['report-schedules'], queryFn: reportsApi.listSchedules });
  const [editing, setEditing] = useState<ScheduledReport | null>(null);
  const [form, setForm] = useState<ScheduledReportInput>(emptyForm());
  const [recipientsText, setRecipientsText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setRecipientsText('');
    setSaveError(null);
    setModalOpen(true);
  };

  const openEdit = (s: ScheduledReport) => {
    setEditing(s);
    setForm({
      name: s.name,
      reportType: s.reportType,
      format: s.format,
      frequency: s.frequency,
      hourUtc: s.hourUtc,
      dayOfWeek: s.dayOfWeek ?? 1,
      dayOfMonth: s.dayOfMonth ?? 1,
      periodDays: s.periodDays,
      recipients: s.recipients,
      enabled: s.enabled,
    });
    setRecipientsText(s.recipients.join(', '));
    setSaveError(null);
    setModalOpen(true);
  };

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    const recipients = recipientsText
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const payload: ScheduledReportInput = {
      ...form,
      recipients,
      dayOfWeek: form.frequency === 'weekly' ? form.dayOfWeek : null,
      dayOfMonth: form.frequency === 'monthly' ? form.dayOfMonth : null,
    };
    try {
      if (editing) await reportsApi.updateSchedule(editing.id, payload);
      else await reportsApi.createSchedule(payload);
      setModalOpen(false);
      await qc.invalidateQueries({ queryKey: ['report-schedules'] });
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSaveError(msg ?? (e instanceof Error ? e.message : 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s: ScheduledReport) => {
    await reportsApi.deleteSchedule(s.id);
    await qc.invalidateQueries({ queryKey: ['report-schedules'] });
  };

  const sendNow = async (s: ScheduledReport) => {
    const r = await reportsApi.testSchedule(s.id);
    setToast(
      r.status === 'success'
        ? `Sent "${s.name}" to ${s.recipients.length} recipient(s).`
        : r.status === 'skipped'
          ? `Not sent — email delivery is disabled (EMAIL_NOTIFICATIONS_ENABLED=false).`
          : `Send failed: ${r.error ?? 'unknown error'}`
    );
    await qc.invalidateQueries({ queryKey: ['report-schedules'] });
    setTimeout(() => setToast(null), 5000);
  };

  return (
    <div className="space-y-6">
      {/* On-demand */}
      <Card>
        <CardHeader title="Generate a report now" subtitle="Download any report as a point-in-time PDF or Excel (covers lakehouses + warehouses)." />
        <CardBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Select
              label="Report"
              value={odType}
              onChange={(e) => setOdType(e.target.value as ReportType)}
              options={typeOptions}
            />
            <Select
              label="Format"
              value={odFormat}
              onChange={(e) => setOdFormat(e.target.value as ReportFormat)}
              options={[
                { value: 'pdf', label: 'PDF' },
                { value: 'excel', label: 'Excel' },
              ]}
            />
            <Input label="From" type="date" value={odStart} onChange={(e) => setOdStart(e.target.value)} />
            <Input label="To" type="date" value={odEnd} onChange={(e) => setOdEnd(e.target.value)} />
            <div className="flex items-end">
              <Button variant="primary" onClick={runDownload} disabled={downloading} className="w-full">
                <Download className="mr-2 h-4 w-4" />
                {downloading ? 'Generating…' : 'Download'}
              </Button>
            </div>
          </div>
          {typeDesc(odType) && <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{typeDesc(odType)}</p>}
          {odError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{odError}</p>}
        </CardBody>
      </Card>

      {/* Scheduled */}
      <Card>
        <CardHeader
          title="Scheduled reports"
          subtitle="Reports generated on a cadence and emailed automatically. Delivery is gated by EMAIL_NOTIFICATIONS_ENABLED."
          action={
            canManage ? (
              <Button variant="primary" size="sm" onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" />
                New schedule
              </Button>
            ) : undefined
          }
        />
        <CardBody>
          {toast && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              <Mail className="h-4 w-4 text-[var(--color-accent)]" />
              {toast}
            </div>
          )}
          {schedules.isLoading ? (
            <LoadingState />
          ) : schedules.isError ? (
            <ErrorState title="Failed to load schedules" />
          ) : (schedules.data ?? []).length === 0 ? (
            <EmptyState
              title="No scheduled reports"
              message="Create a schedule to have any report generated on a cadence and emailed automatically."
            />
          ) : (
            <>
              <ListFilterBar
                query={schedQ}
                onQuery={setSchedQ}
                placeholder="Search reports…"
                count={(schedules.data ?? []).filter((s) => textMatch(schedQ, s.name, s.reportType, s.format)).length}
                total={(schedules.data ?? []).length}
              />
            <DataTable<ScheduledReport>
              rows={(schedules.data ?? []).filter((s) => textMatch(schedQ, s.name, s.reportType, s.format))}
              rowKey={(s) => s.id}
              columns={[
                { key: 'name', header: 'Name', sortable: true, render: (s) => s.name },
                {
                  key: 'reportType',
                  header: 'Report',
                  render: (s) => <Badge variant="info">{s.reportType}</Badge>,
                },
                { key: 'format', header: 'Format', render: (s) => s.format.toUpperCase() },
                { key: 'frequency', header: 'Cadence', render: (s) => frequencySummary(s) },
                { key: 'recipients', header: 'Recipients', render: (s) => `${s.recipients.length}` },
                {
                  key: 'enabled',
                  header: 'Status',
                  render: (s) =>
                    s.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="default">Disabled</Badge>,
                },
                {
                  key: 'nextSendAt',
                  header: 'Next send',
                  render: (s) => (s.nextSendAt ? formatEastern(s.nextSendAt) : '—'),
                },
                {
                  key: 'lastStatus',
                  header: 'Last send',
                  render: (s) =>
                    s.lastStatus ? (
                      <Badge
                        variant={s.lastStatus === 'success' ? 'success' : s.lastStatus === 'failed' ? 'danger' : 'default'}
                      >
                        {s.lastStatus}
                      </Badge>
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  sortable: false,
                  render: (s) =>
                    canManage ? (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => sendNow(s)} title="Send now">
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(s)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[var(--color-text-secondary)]">—</span>
                    ),
                },
              ]}
            />
            </>
          )}
        </CardBody>
      </Card>

      {/* Create / edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit schedule — ${editing.name}` : 'New scheduled report'}
        size="lg"
      >
        <div className="space-y-4">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Report"
              value={form.reportType}
              onChange={(e) => setForm({ ...form, reportType: e.target.value as ReportType })}
              options={typeOptions}
            />
            <Select
              label="Format"
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value as ReportFormat })}
              options={[
                { value: 'pdf', label: 'PDF' },
                { value: 'excel', label: 'Excel' },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Select
              label="Frequency"
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value as ReportFrequency })}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
            />
            <Input
              label="Hour (UTC)"
              type="number"
              value={String(form.hourUtc)}
              onChange={(e) => setForm({ ...form, hourUtc: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
            />
            {form.frequency === 'weekly' && (
              <Select
                label="Day of week"
                value={String(form.dayOfWeek ?? 1)}
                onChange={(e) => setForm({ ...form, dayOfWeek: Number(e.target.value) })}
                options={[
                  { value: '1', label: 'Monday' },
                  { value: '2', label: 'Tuesday' },
                  { value: '3', label: 'Wednesday' },
                  { value: '4', label: 'Thursday' },
                  { value: '5', label: 'Friday' },
                  { value: '6', label: 'Saturday' },
                  { value: '0', label: 'Sunday' },
                ]}
              />
            )}
            {form.frequency === 'monthly' && (
              <Input
                label="Day of month (1–28)"
                type="number"
                value={String(form.dayOfMonth ?? 1)}
                onChange={(e) => setForm({ ...form, dayOfMonth: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })}
              />
            )}
            <Input
              label="Lookback (days)"
              type="number"
              value={String(form.periodDays)}
              onChange={(e) => setForm({ ...form, periodDays: Math.max(1, Math.min(366, Number(e.target.value) || 30)) })}
            />
          </div>
          <Input
            label="Recipients (comma-separated)"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="ops@example.com, manager@example.com"
          />
          <Toggle checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} label="Enabled" />
          {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Create schedule'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ReportsArea;
